import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TimesheetLogic } from '../logic/timesheet.logic';
import { PendingTimesheet } from '../models/pending-timesheets.model';
import { PendingTimesheetsLogic } from '../logic/pending-timesheets.logic';

class PendingTimesheetsState {
    facilityId: string;
    payCycleDate: number;
    unitId: string;
    url: string;
    recipientEmailAddress: string;
    timesheets: PendingTimesheet[];
    changedTimesheets: PendingTimesheet[];
    hospitalName?: string;
    payPeriodStart?: string;
    payPeriodEnd?: string;
    selectAll: boolean;
    selectedTimesheetCount: number;
    showMockData: boolean;
}

const pendingTimesheetsSlice = createSlice({
    name: 'pendingTimesheets',
    initialState: {
        facilityId: '',
        payCycleDate: 0,
        unitId: '',
        url: '',
        recipientEmailAddress: '',
        timesheets: [],
        changedTimesheets: [],
        hospitalName: '',
        payPeriodStart: '',
        payPeriodEnd: '',
        selectAll: false,
        selectedTimesheetCount: 0,
        showMockData: false,
    },
    reducers: {
        setFacilityId(state: PendingTimesheetsState, action: PayloadAction<string>) {
            state.facilityId = action.payload;
        },
        setPayCycleDate(state: PendingTimesheetsState, action: PayloadAction<number>) {
            state.payCycleDate = action.payload || 0;
        },
        setUnitId(state: PendingTimesheetsState, action: PayloadAction<string>) {
            state.unitId = action.payload;
        },
        setUrl(state: PendingTimesheetsState, action: PayloadAction<string>) {
            state.url = action.payload;
        },
        setRecipientEmail(state: PendingTimesheetsState, action: PayloadAction<string>) {
            state.recipientEmailAddress = action.payload;
        },
        setPendingTimesheets(state: PendingTimesheetsState, action: PayloadAction<PendingTimesheet[]>) {
            state.timesheets = TimesheetLogic.removePastApprovedTimesheets(action.payload);
        },
        setChangedPendingTimesheets(state: PendingTimesheetsState, action: PayloadAction<PendingTimesheet[]>) {
            state.changedTimesheets = action.payload;
        },
        setShowMockData(state: PendingTimesheetsState, action: PayloadAction<boolean>) {
            state.showMockData = action.payload;
        },
        setSummaryValues(
            state: PendingTimesheetsState,
            action: PayloadAction<{
                hospitalName: string;
                payPeriodStart: string;
                payPeriodEnd: string;
            }>,
        ) {
            const { hospitalName, payPeriodStart, payPeriodEnd } = action.payload;

            state.hospitalName = hospitalName;
            state.payPeriodStart = payPeriodStart;
            state.payPeriodEnd = payPeriodEnd;
        },
        toggleSelectAll(state: PendingTimesheetsState) {
            const isSelected = !state.selectAll;
            state.selectAll = isSelected;

            for (const timesheet of state.timesheets) {
                timesheet.isSelected = !timesheet.onlineApprovalDate ? isSelected : undefined;
            }

            state.selectedTimesheetCount = PendingTimesheetsLogic.countSelectedTimesheets(state.timesheets);
        },
        toggleSelection(
            state: PendingTimesheetsState,
            action: PayloadAction<{
                timesheet: PendingTimesheet;
                isSelected?: boolean;
            }>,
        ) {
            const { timesheet: incomingTimesheet, isSelected: newSelectedValue } = action.payload;

            const timesheet = state.timesheets.find((item) => item.timesheetId === incomingTimesheet.timesheetId);

            // timesheet will be undefined if it cannot be found.
            // DO NOT suppress the exception - caller is at fault
            const { isSelected: originalSelectedValue } = timesheet;

            let finalSelectedValue = false;

            if (typeof newSelectedValue === 'boolean') {
                finalSelectedValue = newSelectedValue;
            } else if (typeof originalSelectedValue === 'boolean') {
                finalSelectedValue = !originalSelectedValue;
            } else {
                finalSelectedValue = true;
            }

            timesheet.isSelected = finalSelectedValue;

            if (!finalSelectedValue) {
                state.selectAll = false;
            }

            state.selectedTimesheetCount = PendingTimesheetsLogic.countSelectedTimesheets(state.timesheets);
        },
    },
});

export const {
    setFacilityId,
    setUnitId,
    setPayCycleDate,
    setUrl,
    setPendingTimesheets,
    setSummaryValues,
    setChangedPendingTimesheets,
    setShowMockData,
    toggleSelectAll,
    toggleSelection,
} = pendingTimesheetsSlice.actions;

export default pendingTimesheetsSlice.reducer;
